/**
 * RaroOwnerBar.jsx — floating access-level switcher for the venue owner.
 *
 * Renders a small pill in the top-right (below the WhitelabelTopBar and
 * RaroHeader) with quick jumps between:
 *   • Public site   (/)
 *   • Terminal      (/staff)
 *   • Settings      (/staff/settings, manager-only)
 *   • Audit         (/staff/audit, manager-only)
 *
 * Only rendered when the caller is signed in as an owner-level user
 * (agency_owner / admin / business_admin / matched OWNER_EMAILS).
 * When they land on a /staff/* page without a PIN token, tapping
 * Terminal calls /api/venue-pin/impersonate/{vendor_id} to get a
 * manager-role staff JWT so they skip the PIN keypad entirely.
 */
import { useEffect, useState } from "react";
import { resolveRestaurantBusinessId } from "@/lib/hostRegistry";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { KeyRound, Home, LayoutDashboard, Settings, ClipboardList, ChefHat } from "lucide-react";
import { useRaroBrand } from "./RaroBrandContext";
import { STAFF_TOKEN, STAFF_INFO, STAFF_EXP } from "./useStaffSession";

const API       = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const VENDOR_ID =
  (typeof window !== "undefined" &&
    resolveRestaurantBusinessId((window.location.hostname || "").toLowerCase())) ||
  "biz_raro_demo";

// Emails allowed to see the owner switcher regardless of role. Keep
// short — the primary check is role-based.
const OWNER_EMAILS = new Set([
  "dail.wagner@gmail.com",
  "manager@raro.au",
  "admin@bellacucina.com",
  "admin@qrpal.au",
]);

function isOwner(user) {
  if (!user) return false;
  const email = (user.email || "").toLowerCase();
  if (OWNER_EMAILS.has(email)) return true;
  const role = (user.role || "").toLowerCase();
  return ["agency_owner", "admin", "business_admin"].includes(role);
}

export default function RaroOwnerBar() {
  const { brand } = useRaroBrand();
  const location  = useLocation();
  const nav       = useNavigate();
  const [user, setUser] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState("");

  const RED   = brand.brand?.color_primary || "#a01e28";
  const GOLD  = brand.brand?.color_gold    || "#c9a24b";
  const CREAM = brand.brand?.color_cream   || "#fdf6ec";
  const INK   = brand.brand?.color_ink     || "#1a1210";

  useEffect(() => {
    axios.get(`${API}/api/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data || null))
      .catch(() => setUser(null));
  }, []);

  const goStaff = async (target) => {
    setErr("");
    // If we already have a live staff PIN token, just navigate.
    const tok = localStorage.getItem(STAFF_TOKEN);
    const exp = Number(localStorage.getItem(STAFF_EXP) || 0);
    if (tok && exp && exp > Date.now()) {
      nav(target);
      return;
    }
    // Otherwise, impersonate as manager.
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/venue-pin/impersonate/${VENDOR_ID}`,
        {},
        { withCredentials: true });
      localStorage.setItem(STAFF_TOKEN, data.token);
      localStorage.setItem(STAFF_INFO,  JSON.stringify(data.staff));
      localStorage.setItem(STAFF_EXP,   String(Date.now() + (data.session_hours || 8) * 3_600_000));
      nav(target);
    } catch (e) {
      setErr(e.response?.data?.detail || "Impersonation failed");
    } finally { setBusy(false); }
  };

  if (!isOwner(user)) return null;

  const path = location.pathname;
  const on = (p) => path === p || (p !== "/" && path.startsWith(p));

  const pill = (activeMatch) => ({
    display: "inline-flex", alignItems: "center", gap: 5,
    padding: "5px 11px", borderRadius: 999,
    background: activeMatch ? RED : "transparent",
    color: activeMatch ? CREAM : INK,
    border: `1px solid ${activeMatch ? RED : GOLD + "66"}`,
    fontSize: 11, fontWeight: 700, letterSpacing: 1,
    cursor: busy ? "wait" : "pointer",
    textTransform: "uppercase",
    transition: "background 120ms, color 120ms",
  });

  return (
    <div
      data-testid="raro-owner-bar"
      style={{
        position: "fixed",
        top: 88,
        right: 14,
        zIndex: 60,
        background: CREAM,
        border: `1px solid ${GOLD}77`,
        borderRadius: 999,
        boxShadow: "0 6px 20px rgba(0,0,0,.15)",
        padding: "5px 8px",
        display: "flex",
        alignItems: "center",
        gap: 5,
      }}
    >
      <span
        title={`Signed in · ${user?.email || ""}`}
        style={{ display: "inline-flex", alignItems: "center", gap: 4,
                 color: RED, fontWeight: 800, fontSize: 10, letterSpacing: 2,
                 padding: "0 6px 0 4px" }}
      >
        <KeyRound size={12} /> OWNER
      </span>
      <button type="button" onClick={() => nav("/")}
              data-testid="raro-owner-bar-home"
              style={pill(path === "/")}>
        <Home size={12} /> Site
      </button>
      <button type="button" onClick={() => goStaff("/staff")}
              data-testid="raro-owner-bar-terminal" disabled={busy}
              style={pill(path === "/staff")}>
        <LayoutDashboard size={12} /> Terminal
      </button>
      <button type="button" onClick={() => goStaff("/staff/kitchen")}
              data-testid="raro-owner-bar-kitchen" disabled={busy}
              style={pill(on("/staff/kitchen"))}>
        <ChefHat size={12} /> Kitchen
      </button>
      <button type="button" onClick={() => goStaff("/staff/settings")}
              data-testid="raro-owner-bar-settings" disabled={busy}
              style={pill(on("/staff/settings"))}>
        <Settings size={12} /> Settings
      </button>
      <button type="button" onClick={() => goStaff("/staff/audit")}
              data-testid="raro-owner-bar-audit" disabled={busy}
              style={pill(on("/staff/audit"))}>
        <ClipboardList size={12} /> Audit
      </button>
      {err && <span style={{ color: RED, fontSize: 10, marginLeft: 6 }}>{err}</span>}
    </div>
  );
}
